import React from 'react';
import { TagIcon } from '@heroicons/react/24/outline';
import NoImageCircle from '../../../../assets/no-product-image.svg';
import {
  formatOptionName,
  getExtendedOptionPrice,
} from '../../../../utils/optionDisplay';
import { inMenuOrder, optionsInMenuOrder } from '../../../../utils/menu-order';

const usd = (n) =>
  Number(n || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });

const ItemRow = ({ item, itemfee }) => {
  const product = item.item.product;
  const hasVariant = product?.name !== item.name;
  const discounted = Number(item.originaltotal || 0) > Number(item.total || 0);

  return (
    <div className="flex items-start gap-4 px-5 py-4 transition hover:bg-slate-50/60 dark:hover:bg-slate-800/40">
      <img
        className="hidden h-14 w-14 flex-shrink-0 rounded-md object-cover ring-1 ring-slate-100 dark:ring-slate-700 lg:block"
        src={product?.imagepath || NoImageCircle}
        alt={product?.name || ''}
      />

      <div className="flex h-7 min-w-[2.25rem] items-center justify-center rounded-full bg-blue-500/10 text-xs font-bold tabular-nums text-blue-600 dark:bg-sky-400/10 dark:text-sky-400">
        {item.quantity}×
      </div>

      <div className="min-w-0 flex-1">
        <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100">
          {product?.name}
        </h4>
        {hasVariant && (
          <p className="mt-1 text-xs italic text-slate-600 dark:text-slate-300">
            {item.name}
            {item.price + (itemfee || 0) > 0 && (
              <span className="ml-1.5 not-italic font-semibold tabular-nums text-slate-500 dark:text-slate-400">
                · {usd(item.price + (itemfee || 0))}
              </span>
            )}
          </p>
        )}

        {item.orderitemoptions && item.orderitemoptions.length > 0 && (
          <ul className="mt-2 space-y-0.5">
            {optionsInMenuOrder(item.orderitemoptions).map((opt, i) => (
              <li
                key={i}
                className="flex items-baseline gap-1.5 text-xs text-slate-500 dark:text-slate-400"
              >
                <span className="text-slate-300 dark:text-slate-600">•</span>
                <span className="truncate">
                  {formatOptionName(opt, item.quantity)}
                </span>
                {opt.price > 0 && (
                  <span className="italic font-semibold tabular-nums text-slate-700 dark:text-slate-200">
                    {usd(getExtendedOptionPrice(opt, item.quantity))}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}

        {item.note && (
          <p className="mt-2 rounded-md bg-slate-50 px-2 py-1 text-xs italic text-slate-500 dark:bg-slate-700/50 dark:text-slate-400">
            “{item.note}”
          </p>
        )}

        {(item.coupontypelabel === 'Buy X Get Y' ||
          item.coupontypelabel === 'Spend Get Free Item') && (
          <div className="mt-2">
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-900/20 dark:text-emerald-300">
              <TagIcon className="h-3 w-3" />
              {item.coupontypelabel === 'Buy X Get Y'
                ? `Buy ${item.buyItemCount}, Get ${item.getItemCount} Free`
                : 'Free Item Applied'}
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-shrink-0 flex-col items-end">
        {discounted ? (
          <>
            <span className="tabular-nums text-sm font-bold text-emerald-600 dark:text-emerald-400">
              {usd(item.total)}
            </span>
            <span className="tabular-nums text-xs text-slate-400 line-through">
              {usd(item.originaltotal)}
            </span>
          </>
        ) : (
          <span className="tabular-nums text-sm font-bold text-slate-900 dark:text-slate-100">
            {usd(item.total)}
          </span>
        )}
      </div>
    </div>
  );
};

const ItemsList = ({ items, itemfee }) => (
  <section
    className="reveal overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800"
    aria-label="Order items"
  >
    <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3 dark:border-slate-700/60">
      <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[0.65rem] font-semibold tabular-nums text-slate-600 dark:bg-slate-700 dark:text-slate-300">
        {items.reduce((sum, it) => sum + (it.quantity || 0), 0)}
      </span>
      <h3 className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
        Items
      </h3>
    </div>
    <div className="divide-y divide-slate-100 dark:divide-slate-700/60">
      {inMenuOrder(items).map((item, i) => (
        <ItemRow key={i} item={item} itemfee={itemfee} />
      ))}
    </div>
  </section>
);

export default ItemsList;
